import { filter, last, includes, isEmpty } from "lodash";

export const messages = {
  notFoundError: "Not Found",
  unauthorized: "Unauthorized",
};

export const ERROR_CODES = {
  WORKSPACE_NOT_FOUND: "WORKSPACE_NOT_FOUND",
};

/**
 * Best user-facing message for membership/allocation mutations.
 * Uses backend error message (GraphQL/API) which includes access control errors:
 * manager-only, team hierarchy, access group hierarchy.
 * @param {Error & { graphQLErrors?: Array<{ message?: string }>, message?: string }} error
 * @returns {string}
 */
export const getMembershipOrAllocationErrorMessage = (error) => {
  if (!error) {
    return "";
  }
  const graphQLMessage = error?.graphQLErrors?.[0]?.message;
  return graphQLMessage || error?.message || String(error);
};

export const throwNotFoundError = (details) => {
  const notFoundError = new Error(messages.notFoundError);
  notFoundError.details = details;
  throw notFoundError;
};

export class UserMessageError extends Error {
  constructor(message, details, actionLink, options = {}) {
    super(message);
    this.name = "UserMessageError";
    this.details = details;
    this.actionLink = actionLink; // { label: string, path: string }
    this.title = options.title; // Custom title (optional)
    this.icon = options.icon; // Custom icon (optional) - FontAwesome icon array format
    this.iconColor = options.iconColor; // Custom icon color (optional) - theme color name
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UserMessageError);
    }
  }
}

export const isUserMessageError = (error) => {
  return (
    error instanceof UserMessageError || error?.name === "UserMessageError"
  );
};

const hasNonTagsError = (error) =>
  filter(error?.graphQLErrors, (graphQLError) => {
    return last(graphQLError?.path) !== "tags";
  }).length !== 0;

export const canContinue = (error) => {
  // if error is just related to tags we can continue
  const canContinueFromError =
    !error ||
    (!error?.networkError &&
      !isEmpty(error?.graphQLErrors) &&
      !hasNonTagsError(error));

  return canContinueFromError;
};

export const throwIfRequired = (error) => {
  if (!canContinue(error)) {
    throw error;
  }
};

export const isForbiddenError = (error) => {
  return (
    error?.networkError?.response?.status === 403 ||
    includes(error?.message, messages.unauthorized)
  );
};

export const isUnderMaintenance = (error) => {
  return error?.networkError?.response?.status === 503;
};

export const isServerError = (error) => {
  return (
    !isEmpty(error?.graphQLErrors) ||
    (error?.networkError &&
      !isForbiddenError(error) &&
      !isUnderMaintenance(error))
  );
};

export const isNotFoundError = (error) => {
  return error?.message === messages.notFoundError;
};

export const isWorkspaceNotFoundError = (error) => {
  // Apollo Server v5 + HTTP-200 responses: errors arrive as graphQLErrors.
  // Apollo Server v3 with 4xx responses: errors were mapped to networkError.result.errors
  // (https://github.com/apollographql/apollo-client/issues/6222#issuecomment-781518783).
  // Check both paths so this works regardless of server version.
  const hasGraphQLError = (error?.graphQLErrors || []).some(
    (err) => err?.extensions?.code === ERROR_CODES.WORKSPACE_NOT_FOUND
  );
  const hasNetworkError = (error?.networkError?.result?.errors || []).some(
    (err) => err?.extensions?.code === ERROR_CODES.WORKSPACE_NOT_FOUND
  );
  return hasGraphQLError || hasNetworkError;
};

export const isAuth0Error = (errorContext) => {
  return errorContext?.source === "auth0";
};

export const isAuthorizationError = (errorContext) => {
  return isAuth0Error(errorContext) && errorContext?.error === "unauthorized";
};

export function errorInScope(error) {
  if (isUnderMaintenance(error)) {
    return;
  }
  if (isWorkspaceNotFoundError(error)) {
    return;
  }
  if (isNotFoundError(error)) {
    return;
  }
  throw error;
}
